import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Search, X } from "lucide-react";

/**
 * Searchable single-select combobox. Keyboard-friendly:
 *   ↑/↓ navigate, Enter select, Esc close, Backspace clears query.
 *
 * Props:
 *   options          : (string | {value, label, group?})[]
 *   value            : string                         (current value)
 *   onChange         : (value: string) => void
 *   placeholder      : string                         (input placeholder)
 *   emptyLabel       : string                         (no-results message)
 *   pinnedValues     : string[]                       (rendered first when query is empty)
 *   pinnedLabel      : string                         (e.g. "Most common")
 *   id, "data-testid": string
 *   error            : boolean                        (red border)
 */
export function Combobox({
  options = [],
  value = "",
  onChange,
  placeholder = "Search…",
  emptyLabel = "No matches",
  pinnedValues = [],
  pinnedLabel = "Suggested",
  id,
  "data-testid": testId,
  error = false,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const ref = useRef(null);
  const listRef = useRef(null);

  // Normalise options into {value, label}
  const norm = useMemo(
    () =>
      options.map((o) =>
        typeof o === "string" ? { value: o, label: o } : o,
      ),
    [options],
  );

  // When closed, clear query so reopening shows the full list
  useEffect(() => {
    if (!open) setQuery("");
    else setActive(0);
  }, [open]);

  // Close on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const selected = norm.find((o) => o.value === value);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      if (!pinnedValues?.length) return norm;
      const pinned = pinnedValues
        .map((v) => norm.find((o) => o.value === v))
        .filter(Boolean)
        .map((o) => ({ ...o, _pinned: true }));
      const rest = norm.filter((o) => !pinnedValues.includes(o.value));
      return [...pinned, ...rest];
    }
    return norm.filter((o) => o.label.toLowerCase().includes(q));
  }, [norm, query, pinnedValues]);

  const choose = (val) => {
    onChange?.(val);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActive((a) => Math.min(a + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[active]) choose(filtered[active].value);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  // Scroll active item into view
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-i='${active}']`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        id={id}
        type="button"
        data-testid={testId}
        onClick={() => setOpen((o) => !o)}
        className={`w-full h-12 pl-4 pr-9 rounded-xl border bg-white text-left flex items-center justify-between transition-all focus:outline-none focus:ring-4 focus:ring-[var(--lime)]/20 text-[15px] ${
          error
            ? "border-red-300 text-red-700"
            : selected
            ? "border-[var(--light-border)] text-[var(--charcoal)]"
            : "border-[var(--light-border)] text-[var(--text-muted)]"
        } ${open ? "border-[var(--lime)] ring-4 ring-[var(--lime)]/20" : ""}`}
      >
        <span className="truncate">
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown
          size={16}
          className={`flex-shrink-0 text-[var(--text-muted)] transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open && (
        <div className="absolute left-0 right-0 mt-2 z-30 bg-white rounded-xl border border-[var(--light-border)] shadow-xl overflow-hidden">
          <div className="relative border-b border-[var(--light-border)]">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] pointer-events-none"
            />
            <input
              autoFocus
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActive(0);
              }}
              onKeyDown={onKeyDown}
              placeholder={placeholder}
              data-testid={testId ? `${testId}-search` : undefined}
              className="w-full h-10 pl-9 pr-9 text-sm bg-transparent text-[var(--charcoal)] placeholder:text-[var(--text-muted)] focus:outline-none"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-[var(--bone-soft)]"
              >
                <X size={14} className="text-[var(--text-muted)]" />
              </button>
            )}
          </div>

          <ul
            ref={listRef}
            role="listbox"
            className="max-h-64 overflow-y-auto py-1"
          >
            {filtered.length === 0 && (
              <li className="px-4 py-6 text-center text-sm text-[var(--text-muted)]">
                {emptyLabel}
              </li>
            )}

            {filtered.map((o, i) => {
              const isFirstAfterPinned =
                pinnedValues?.length &&
                !query &&
                i === pinnedValues.filter((v) =>
                  norm.find((x) => x.value === v),
                ).length;
              return (
                <li key={o.value} role="option" aria-selected={value === o.value}>
                  {isFirstAfterPinned && (
                    <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] px-3 pt-2 pb-1">
                      All conditions
                    </div>
                  )}
                  {o._pinned && i === 0 && pinnedLabel && (
                    <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] px-3 pt-2 pb-1">
                      {pinnedLabel}
                    </div>
                  )}
                  <button
                    type="button"
                    data-i={i}
                    data-testid={
                      testId ? `${testId}-option-${o.value}` : undefined
                    }
                    onMouseEnter={() => setActive(i)}
                    onClick={() => choose(o.value)}
                    className={`w-full text-left px-4 py-2 text-sm flex items-center justify-between transition-colors ${
                      i === active
                        ? "bg-[var(--teal-soft)] text-[var(--teal-dark)]"
                        : "text-[var(--charcoal)]"
                    }`}
                  >
                    <span className="truncate">{o.label}</span>
                    {value === o.value && (
                      <Check size={14} className="text-[var(--teal)] flex-shrink-0" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

export default Combobox;
